import React, { CSSProperties, useCallback } from 'react'
import { Token } from '@nominex/nomiswap-sdk'
import tw from 'twin.macro'
import styled from 'styled-components'
import { SlButton, SlIcon } from '@shoelace-style/shoelace/dist/react'
import { CheckmarkCircleIcon, Text } from '@nominex/dex-uikit'
import { AutoColumn, AutoRow, RowFixed } from 'components/Layout'
import { CurrencyImage } from 'components/CurrencyImage'
import { useWagmi } from 'libs/wagmi'
import { useCombinedInactiveList } from 'state/lists/hooks'
import { useIsTokenActive, useIsUserAddedToken } from 'hooks/Tokens'
import { useTranslation } from 'contexts/Localization'

const CurrencyRowItem = styled.div`
  ${tw`text-white grid gap-8 items-center px-20 py-4 hover:bg-dark-gray-900 h-[56px] cursor-pointer`}
  grid-template-columns: auto minmax(auto, 1fr) auto;
`

const CheckIcon = styled(CheckmarkCircleIcon)`
  height: 16px;
  width: 16px;
  margin-right: 6px;
  stroke: ${({ theme }) => theme.colors.success};
`

const NameOverflow = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 140px;
  font-size: 12px;
`

export default function ImportRow({
  token,
  style,
  dim,
  showImportView,
  setImportToken,
}: {
  token: Token
  style?: CSSProperties
  dim?: boolean
  showImportView: () => void
  setImportToken: (token: Token) => void
}) {
  // globals
  const { chainId } = useWagmi()

  const { t } = useTranslation()

  // check if token comes from list
  const inactiveTokenList = useCombinedInactiveList()
  const list = chainId && inactiveTokenList?.[chainId]?.[token.address]?.list

  // check if already active on list or local storage tokens
  const isAdded = useIsUserAddedToken(token)
  const isActive = useIsTokenActive(token)

  const handleImport = useCallback(() => {
    if (setImportToken) {
      setImportToken(token)
    }

    showImportView()
  }, [setImportToken, showImportView, token])

  return (
    <CurrencyRowItem style={style}>
      <CurrencyImage currency={token} size="32px" className={dim ? 'opacity-60' : ''} />
      <AutoColumn style={{ opacity: dim ? '0.6' : '1' }}>
        <AutoRow>
          <Text>{token.symbol}</Text>
          <Text color="textDisabled" ml="8px">
            <NameOverflow title={token.name}>{token.name}</NameOverflow>
          </Text>
        </AutoRow>
        {list && list.logoURI && (
          <RowFixed>
            <Text small mr="4px" color="textSubtle">
              {t('via')} {list.name}
            </Text>
          </RowFixed>
        )}
      </AutoColumn>
      {!isActive && !isAdded ? (
        <SlButton variant="text" onClick={handleImport}>
          <SlIcon name="import" library="unicons" slot="prefix" />
          {t('Import')}
        </SlButton>
      ) : (
        <RowFixed style={{ minWidth: 'fit-content' }}>
          <CheckIcon />
          <Text color="success">Active</Text>
        </RowFixed>
      )}
    </CurrencyRowItem>
  )
}
